import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSidebar } from "../../SidebarContext";
import { decryptToken } from "../utils/getToken";

const CareerFormWithJobs = () => {
    const [formData, setFormData] = useState({
        CandidateName: "",
        CandidateEmail: "",
        CandidatePhone: "",
        Address: "",
        Resume: null,
        Role: "",
        Experience: "",
        ProfileLink: "",
    });
    const { isSidebarOpen } = useSidebar();
    const apiUrl = process.env.REACT_APP_LINK;
    const token = decryptToken("accessToken") || ""
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const res = await fetch(`${apiUrl}/jobs`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                });
                const data = await res.json();
                setJobs(data);
            } catch (err) {
                console.error("Failed to fetch jobs:", err);
                toast.error("Failed to load job listings.");
            }
        };

        fetchJobs();
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "Resume") {
            setFormData({ ...formData, Resume: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleApply = (job) => {
        setFormData((prev) => ({
            ...prev,
            Role: job.role,
            Experience: job.experience,
        }));
        toast.info(
            `Fill the candidate application form for ${job.role} (${job.experience} exp)`,
            {
                position: "top-right",
                autoClose: 3000,
                pauseOnHover: true,
                closeOnClick: true,
                theme: "colored",
            }
        );
    };

    const resetForm = () => {
        setFormData({
            CandidateName: "",
            CandidateEmail: "",
            CandidatePhone: "",
            Address: "",
            Resume: null,
            Role: "",
            Experience: "",
            ProfileLink: "",
        });
        document.getElementById("Resume").value = "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formDataToSend = new FormData();
        for (let key in formData) {
            formDataToSend.append(key, formData[key]);
        }

        try {
            const res = await fetch(`${apiUrl}/career`, {
                method: "POST",
                body: formDataToSend,
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await res.json();
            if (res.ok) {
                toast.success("Submitted successfully! Email confirmation sent.");
                resetForm();
                setTimeout(() => navigate("/onboarding"), 2000);
            } else {
                toast.error(data.message || "Error submitting form.");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Error submitting form.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`open-close ${isSidebarOpen ? "open" : "closed"}`}>
            <div className="flex flex-wrap gap-6 min-h-screen text-sm">
                {/* Job Cards */}
                <div className="flex-1 min-w-[300px]">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Available Jobs</h2>
                    <div className="grid gap-5">
                        {jobs.length > 0 ? (
                            jobs.map((job, index) => (
                                <div
                                    key={index}
                                    className="p-5 rounded-2xl shadow-md bg-white border hover:shadow-lg hover:border-blue-500 transition"
                                >
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">{job.role}</h3>
                                    <p className="text-sm text-gray-600 mb-1">
                                        <strong>Experience:</strong> {job.experience}
                                    </p>
                                    <p className="text-sm text-gray-600 mb-1">
                                        <strong>Salary:</strong> ₹{job.salary}
                                    </p>
                                    <p className="text-sm text-gray-600 mb-3">
                                        <strong>Description:</strong> {job.jobDescription}
                                    </p>
                                    <button
                                        className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:bg-blue-300 disabled:cursor-not-allowed"
                                        onClick={() => handleApply(job)}
                                    >
                                        Apply Now
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500 text-sm">Loading jobs...</p>
                        )}
                    </div>
                </div>


                {/* Candidate Form */}
                <div className="flex-1 min-w-[300px]">
                    <h2 className="text-lg font-semibold mb-2 text-gray-800">Candidate Application Form</h2>
                    <form
                        onSubmit={handleSubmit}
                        encType="multipart/form-data"
                        className="space-y-3 bg-white shadow-sm p-4 rounded-xl border"
                    >
                        <div>
                            <label className="block text-xs font-medium text-gray-700">Candidate Name</label>
                            <input
                                name="CandidateName"
                                placeholder="Enter full name"
                                value={formData.CandidateName}
                                onChange={handleChange}
                                required
                                className="w-full border rounded-md p-2 mt-1 focus:ring-1 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700">Email</label>
                            <input
                                name="CandidateEmail"
                                type="email"
                                placeholder="Enter email address"
                                value={formData.CandidateEmail}
                                onChange={handleChange}
                                required
                                className="w-full border rounded-md p-2 mt-1 focus:ring-1 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700">Phone Number</label>
                            <input
                                name="CandidatePhone"
                                type="tel"
                                placeholder="Enter contact number"
                                value={formData.CandidatePhone}
                                onChange={handleChange}
                                required
                                className="w-full border rounded-md p-2 mt-1 focus:ring-1 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700">Address</label>
                            <textarea
                                name="Address"
                                placeholder="Enter residential address"
                                value={formData.Address}
                                onChange={handleChange}
                                required
                                className="w-full border rounded-md p-2 mt-1 focus:ring-1 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700">Role Applied For</label>
                            <input
                                name="Role"
                                placeholder="Enter job role"
                                value={formData.Role}
                                onChange={handleChange}
                                required
                                className="w-full border rounded-md p-2 mt-1 focus:ring-1 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700">Experience (in years)</label>
                            <input
                                name="Experience"
                                placeholder="Enter years of experience"
                                value={formData.Experience}
                                onChange={handleChange}
                                required
                                className="w-full border rounded-md p-2 mt-1 focus:ring-1 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700">LinkedIn / GitHub URL</label>
                            <input
                                name="ProfileLink"
                                placeholder="Enter LinkedIn or GitHub profile link"
                                value={formData.ProfileLink}
                                onChange={handleChange}
                                className="w-full border rounded-md p-2 mt-1 focus:ring-1 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label htmlFor="Resume" className="block text-xs font-medium text-gray-700">
                                Upload Resume <span className="text-gray-500">(PDF, DOC, DOCX)</span>
                            </label>
                            <input
                                id="Resume"
                                name="Resume"
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={handleChange}
                                required
                                className="w-full border rounded-md p-2 mt-1 file:mr-2 file:py-1 file:px-3 file:border-0 file:rounded-md file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer text-xs"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:bg-blue-300 disabled:cursor-not-allowed"
                        >
                            {loading ? "Submitting..." : "Submit Application"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CareerFormWithJobs;
